import { useMemo } from 'react'
import { useFormContext, useWatch } from 'react-hook-form'

import {
  DEFAULT_QUERY_STATUS_CHECKS_STALE_TIME,
  convertDatadogAppUrlToApiUrl,
  type StatusCheckFormValues
} from 'components/status-checks/status-checks-form-utils'
import { getExistingMonitorUrlPredicateFn } from 'components/status-checks/status-checks-form-validation-utils'
import { useStatusCheckDetailsContext } from 'context/status-checks/StatusCheckDetailsContext'
import { useGetStatusChecksAsServiceStatusChecks } from 'queries/status-checks/use-get-status-checks-as-service-status-checks'
import { ObservabilityToolType } from 'types/api/status-checks/status-check'

const fieldsToWatch = [
  'observabilityTool',
  'monitorUrl',
  'endpointConfiguration.method'
] as const

/**
 * Certain integration URLs may be expressed in various formats but can point to
 * the same monitor, so we first need to normalize the format for accurate comparison.
 */
function normalizeMonitorUrlForComparison(
  monitorUrl: string | undefined,
  observabilityToolType: ObservabilityToolType
) {
  const isDatadogObservabilityTool =
    observabilityToolType === ObservabilityToolType.DATADOG

  return isDatadogObservabilityTool
    ? convertDatadogAppUrlToApiUrl(monitorUrl)
    : monitorUrl
}

/**
 * The hook reads the current form and edit-session state directly.
 * Callers only ask for the current match and do not pass lookup data around.
 */
export function useGetExistingStatusChecksByMonitorUrl() {
  const { control } = useFormContext<StatusCheckFormValues>()
  const { statusCheckId } = useStatusCheckDetailsContext()
  const [observabilityTool, monitorUrl, requestMethod] = useWatch({
    control,
    name: fieldsToWatch
  })

  const { data: existingStatusChecks, isSuccess } =
    useGetStatusChecksAsServiceStatusChecks({
      enabled: Boolean(monitorUrl),
      retryOnMount: false,
      staleTime: DEFAULT_QUERY_STATUS_CHECKS_STALE_TIME
    })

  return useMemo(() => {
    const { type: observabilityToolType } = observabilityTool

    // Bail early if we don't have the necessary info to filter current existing status checks.
    if (!isSuccess || !monitorUrl || !observabilityToolType) return

    /*
     * Predicates differ by observability tool type, and are ultimately used to
     * determine  whether existing status checks should be considered a match.
     */
    const existingStatusPredicateFn = getExistingMonitorUrlPredicateFn(
      observabilityToolType
    )

    const normalizedMonitorUrl = normalizeMonitorUrlForComparison(
      monitorUrl,
      observabilityToolType
    )

    return existingStatusChecks.filter((existingStatusCheck) => {
      // Filter out the current status check in the 'edit' case from the results.
      if (statusCheckId && existingStatusCheck.id === statusCheckId) return

      // Also filter out items that don't pass the predicate, which is tool type dependent.
      if (
        !existingStatusPredicateFn(
          existingStatusCheck,
          observabilityTool,
          requestMethod
        )
      )
        return

      const { integration } = existingStatusCheck
      const { observabilityToolType, url } = integration

      /*
       * This is probably a gap in our type accuracy. It doesn't make sense that an
       * integration has no defined observability tool type.
       */
      if (!observabilityToolType) return

      const normalizedComparisonMonitorUrl = normalizeMonitorUrlForComparison(
        url,
        observabilityToolType
      )

      return normalizedComparisonMonitorUrl === normalizedMonitorUrl
    })
  }, [
    existingStatusChecks,
    isSuccess,
    observabilityTool,
    monitorUrl,
    requestMethod,
    statusCheckId
  ])
}
